import type { ChangeEvent } from 'react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Form, useFormikContext } from 'formik'

import { DatePickerInput, NumberFieldInput, dateConversions } from '@dhl/inputs'
import { TextFieldInput } from '@dhl/formik'
import { Box, Button, Grid, H6, Switch } from '@dhl-ui/components'

import { DHL_EXPRESS_COUNTRIES as COUNTRIES } from '~/common/config/country'
import { MultiTagAutoComplete } from '~/ui-lib'
import { getSelectedLabelValues, type LabelValue } from '~/common/model'
import type { PromoCode } from '~/modules/promocodes/model'
import { links } from '~/AppRoutes'

export interface PromoCodeFormProps {
  id?: string
  handleChangeSwitch: (event: boolean) => void
}

export const PARCEL: LabelValue[] = [
  { value: 'DHL-PARCEL', label: 'DHL-PARCEL' },
  { value: 'DHL-EXPRESS', label: 'DHL-EXPRESS' },
]

export const PromoCodeForm = ({ id, handleChangeSwitch }: PromoCodeFormProps) => {
  const { t } = useTranslation()
  const addNew = !id
  const {
    setFieldValue,
    values: { fromCountries = [], toCountries = [], carriers = [] },
  } = useFormikContext<PromoCode>()

  const navigate = useNavigate()
  const [generatePromocodes, setGeneratePromocodes] = useState<boolean>(false)

  const countriesFrom = !fromCountries.length
    ? COUNTRIES
    : COUNTRIES.filter((country) => !fromCountries.includes(country.value))

  const countriesTo = !toCountries.length
    ? COUNTRIES
    : COUNTRIES.filter((country) => !toCountries.includes(country.value))

  const selectedParcel = !carriers.length
    ? PARCEL
    : PARCEL.filter((parcelOption) => !carriers.includes(parcelOption.value))

  const selectedFromCountries = getSelectedLabelValues(fromCountries, COUNTRIES)
  const selectedToCountries = getSelectedLabelValues(toCountries, COUNTRIES)
  const selectedCarriers = getSelectedLabelValues(carriers, PARCEL)

  const handleChange = (event: ChangeEvent<{ checked: boolean }>) => {
    setGeneratePromocodes(event.target.checked)
    handleChangeSwitch(event.target.checked)
  }

  return (
    <Form>
      <Box pb={2}>
        <Switch name="generatePromocodes" onChange={handleChange} /> {t('admin.promoCodesPage.promoCodes')}
        <Grid container direction="row" spacing={2}>
          <Grid size={{ sm: 12 }}>
            {generatePromocodes ? (
              <NumberFieldInput fullWidth label={t('admin.promoCodesPage.promoCodeForm.amountOfCode')} name="number" />
            ) : (
              <TextFieldInput
                disabled={!addNew}
                fullWidth
                label={t('admin.promoCodesPage.promoCodeForm.code')}
                name="key"
                type="text"
              />
            )}
          </Grid>
          <Grid size={{ sm: 12 }}>
            <H6>{t('admin.promoCodesPage.promoCodeForm.subtitle')}</H6>
          </Grid>
          <Grid size={{ sm: 6 }}>
            <NumberFieldInput
              fullWidth
              label={t('admin.promoCodesPage.promoCodeForm.discount')}
              name="discountOnShippingCosts"
            />
          </Grid>
          <Grid size={{ sm: 6 }}>
            <NumberFieldInput fullWidth label={t('admin.promoCodesPage.promoCodeForm.maxUsages')} name="maxUses" />
          </Grid>
          <Grid size={{ sm: 6 }}>
            <MultiTagAutoComplete
              value={selectedFromCountries}
              label={t('admin.promoCodesPage.promoCodeForm.countryFrom')}
              options={countriesFrom}
              onChange={(value) => setFieldValue('fromCountries', value)}
            />
          </Grid>
          <Grid size={{ sm: 6 }}>
            <MultiTagAutoComplete
              value={selectedToCountries}
              label={t('admin.promoCodesPage.promoCodeForm.countryTo')}
              options={countriesTo}
              onChange={(value) => setFieldValue('toCountries', value)}
            />
          </Grid>
          <Grid size={{ sm: 12 }}>
            <MultiTagAutoComplete
              value={selectedCarriers}
              label={t('admin.promoCodesPage.promoCodeForm.carriers')}
              options={selectedParcel}
              onChange={(value) => setFieldValue('carriers', value)}
            />
          </Grid>
          <Grid size={{ sm: 6 }}>
            <DatePickerInput
              variant="outlined"
              name="validFrom"
              label={t('admin.promoCodesPage.promoCodeForm.validFrom')}
              conversions={dateConversions}
              fullWidth
            />
          </Grid>
          <Grid size={{ sm: 6 }}>
            <DatePickerInput
              variant="outlined"
              name="validUntil"
              label={t('admin.promoCodesPage.promoCodeForm.validUntil')}
              conversions={dateConversions}
              fullWidth
            />
          </Grid>
        </Grid>
      </Box>
      <Box display="flex" justifyContent="flex-end" gap={1}>
        <Button
          color="primary"
          variant="text"
          size="medium"
          onClick={() => navigate(id ? links.promoCodeDetailsLink(id) : links.promoCodesLink())}
        >
          {t('admin.promoCodesPage.cancel')}
        </Button>
        <Button type="submit" color="primary" variant="contained" size="medium">
          {t('admin.promoCodesPage.promoCodeForm.saveButton')}
        </Button>
      </Box>
    </Form>
  )
}
